import "react-loading-skeleton/dist/skeleton.css";
import "../amplify_config";
import "../styles/applications.scss";

import { HubCapsule } from "@aws-amplify/core";
import { Authenticator } from "@aws-amplify/ui-react";
import { Hub } from "aws-amplify";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Provider } from "react-redux";

// Types
import { AppPropsWithLayout, AuthFailures, GetLayout } from "../types";

// State
import { fetchUserAsync } from "../app/signedInUserSlice";
import store from "../app/store";

// Components
import { AuthenticatedLayout } from "../components/layouts/AuthenticatedLayout";
import * as Bootstrap from "../components/Bootstrap";

const authFailures: AuthFailures = {
  signIn: [],
  tokenRefresh: [],
};

const listener = ({ payload }: HubCapsule) => {
  const { event, message, data } = payload;

  switch (event) {
    case "signIn_failure":
      authFailures.signIn.push(data instanceof Error ? data.message : message);
      break;
    case "tokenRefresh_failure":
      authFailures.tokenRefresh.push(message);
      break;
    case "tokenRefresh":
      store.dispatch(fetchUserAsync(false));
      break;
  }
};

Hub.listen("auth", listener);

const genView = (view: ReactNode): ReactNode =>
  process.env.NODE_ENV !== "development" ? (
    view
  ) : (
    <Bootstrap.SSRProvider>{view}</Bootstrap.SSRProvider>
  );

const MyApp = ({ Component, pageProps }: AppPropsWithLayout) => {
  const getLayout = useMemo<GetLayout>(
    () =>
      Component.getLayout ||
      ((page) => <AuthenticatedLayout>{page}</AuthenticatedLayout>),
    [Component.getLayout]
  );

  const [shouldRender, setShouldRender] = useState<boolean>(
    process.env.NEXT_PUBLIC_API_MOCKING !== "enabled"
  );

  useEffect(() => {
    async function initMocks() {
      const { initMocks } = await import("../mocks");
      await initMocks();
      setShouldRender(true);
    }

    if (process.env.NEXT_PUBLIC_API_MOCKING === "enabled") {
      // noinspection JSIgnoredPromiseFromCall
      initMocks();
    }
  }, []);

  if (!shouldRender) {
    return null;
  }

  return genView(
    <Authenticator.Provider>
      <Provider store={store}>
        {getLayout(<Component {...pageProps} authFailures={authFailures} />)}
      </Provider>
    </Authenticator.Provider>
  );
};

// noinspection JSUnusedGlobalSymbols
export default MyApp;
